import saveApplicationAndLead from "@/lib/saveApplicationAndLead";
import { WithExplicitOptIn } from "@/lib/types";
import { logger } from "@justhome/common";
import { EMAIL_PATTERN, PHONE_PATTERN } from "@justhome/common/constants";
import {
  Button,
  FormLegalDisclaimer,
  getFormConsentText,
  Icon,
  Locale,
  TextField,
  Title,
} from "@justhome/react-ui";
import { useMutation } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import i18n from "../../lib/i18n";
import useAppContext from "../../lib/useAppContext";
import useUtmParams from "../../lib/useUtm";

const ContactStep: FC = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const utmParams = useUtmParams();

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm<WithExplicitOptIn>({
    defaultValues: state.contactForm,
  });

  const { isError, isLoading, mutateAsync } = useMutation({
    mutationFn: saveApplicationAndLead,
    onSuccess({ lead, application }) {
      dispatch({ type: "SET_LEAD", lead });
      dispatch({ type: "SET_APPLICATION", application });
    },
    onError(error) {
      dispatch({ type: "POP_STEP" });
      logger.error("Failed to save application and lead", error);
    },
  });

  const onSubmit = useCallback(
    async (contactForm: WithExplicitOptIn) => {
      const { explicitOptIn: _, ...values } = contactForm;
      const consentText = getFormConsentText({
        locale: i18n.language as Locale,
        partner: state.partner,
        requireContactSharingOptIn: true,
      });

      await mutateAsync({
        contactForm: values,
        form: state.form!,
        partnerId: state.partner?.id,
        origin: [utmParams],
        consentText,
      });

      dispatch({ type: "ADD_STEP", step: "offers" });
    },
    [dispatch, mutateAsync, state.form, state.partner, utmParams],
  );

  const onBack = useCallback(() => {
    dispatch({ type: "POP_STEP" });
  }, [dispatch]);

  return (
    <div className="grid grid-cols-12 gap">
      <div className="col-span-12 s:col-span-6 m:col-span-6 m:col-start-2">
        <Title as="h3" className="text-h2 mb-m">
          {t("contact.title")}
        </Title>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 m:grid-cols-2 gap-s m:gap mb-3xs items-start">
            <TextField
              {...register("firstName", {
                required: {
                  value: true,
                  message: t("contact.firstNameRequired"),
                },
              })}
              errorMessage={errors.firstName?.message}
              autoFocus
              label={t("contact.firstName")}
              data-testid="firstName"
            />
            <TextField
              {...register("lastName", {
                required: {
                  value: true,
                  message: t("contact.lastNameRequired"),
                },
              })}
              errorMessage={errors.lastName?.message}
              label={t("contact.lastName")}
              data-testid="lastName"
            />
            <TextField
              {...register("email", {
                required: {
                  value: true,
                  message: t("contact.emailRequired"),
                },
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t("contact.emailInvalid"),
                },
              })}
              label={t("contact.email")}
              errorMessage={errors.email?.message}
              data-testid="email"
            />
            <TextField
              {...register("phone", {
                required: {
                  value: true,
                  message: t("contact.phoneRequired"),
                },
                pattern: {
                  value: PHONE_PATTERN,
                  message: t("contact.phoneInvalid"),
                },
              })}
              type="phone"
              label={t("contact.phone")}
              errorMessage={errors.phone?.message}
              data-testid="phone"
            />
          </div>
          <div className="col-span-2 mb-2xs">
            <FormLegalDisclaimer
              locale={i18n.language as Locale}
              partner={state.partner}
              requireContactSharingOptIn
              control={control}
              optInFieldName="explicitOptIn"
            />
          </div>
          {isError && (
            <div className="mt-m flex rounded-s border border-error-200 bg-error-50 px-s py-3xs text-body-2">
              <Icon
                type="Attention"
                className="text-error-700 mr-2xs mt-xs"
                size="16"
              />
              {t("contact.error")}
            </div>
          )}
          <div className="flex gap mt-3s">
            <Button variant="ghost" paddingX={false} size="md" onClick={onBack}>
              {t("contact.back")}
            </Button>
            <Button
              iconEnd="Right"
              size="md"
              type="submit"
              data-testid="submit"
              isLoading={isLoading}
            >
              {t("contact.continue")}
            </Button>
          </div>
        </form>
      </div>
      <div className="hidden s:flex s:col-span-4 s:col-start-8 m:col-start-9 m:col-span-3 self-center justify-end">
        <div className="max-w-[220px] w-full">
          <img
            width="220"
            height="435"
            src="https://cdn.justhome.com/cdn/assets/application_phone_438b5cfa26.png"
            alt=""
          />
        </div>
      </div>
    </div>
  );
};

export default ContactStep;
